import { Settings } from "./settings";

export class MediaRecorderManager {
  constructor(videoProcessor) {
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.videoProcessor = videoProcessor;
    this.audioVideoStream = null;
    this.canvasStream = null;
    this.audioContext = null;
    this.monitorNode = null;
    this.isRecording = false;
    this.dataAvailableInterval = null;
    this.lensAudioTracks = [];
    this.micSourceNode = null;
    this.lensSourceNodes = [];
    this.mixedDestination = null;
  }

  async startRecording(liveRenderTarget, constraints, isMicOn) {
    if (this.isRecording) {
      console.warn("Already recording, ignoring startRecording call");
      return false;
    }

    try {
      this.resetRecordingVariables();

      // Capture media streams
      this.audioVideoStream = await navigator.mediaDevices.getUserMedia(constraints);
      this.canvasStream = liveRenderTarget.captureStream(Settings.recording.fps);

      // --- Create a single AudioContext and MediaStreamDestination for mixing ---
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.mixedDestination = this.audioContext.createMediaStreamDestination();
      this.lensSourceNodes = [];

      // --- Add lens audio (if any) ---
      if (global && global.monitorNodes && global.monitorNodes.length > 0) {
        global.monitorNodes.forEach((monitorNode) => {
          if (monitorNode && monitorNode.stream) {
            const lensAudioTrack = monitorNode.stream.getAudioTracks()[0];
            if (lensAudioTrack) {
              const lensSource = this.audioContext.createMediaStreamSource(new MediaStream([lensAudioTrack]));
              lensSource.connect(this.mixedDestination);
              this.lensSourceNodes.push(lensSource);
            }
          }
        });
      }

      // --- Add mic audio (if enabled) ---
      if (isMicOn) {
        const micTrack = this.audioVideoStream.getAudioTracks()[0];
        if (micTrack) {
          this.micSourceNode = this.audioContext.createMediaStreamSource(new MediaStream([micTrack]));
          this.micSourceNode.connect(this.mixedDestination);
        }
      }

      // --- Add the mixed audio track to the canvas stream ---
      const mixedTrack = this.mixedDestination.stream.getAudioTracks()[0];
      if (mixedTrack) {
        this.canvasStream.addTrack(mixedTrack);
      }

      const options = {
        mimeType: Settings.recording.mimeType,
        videoBitsPerSecond: 10000000,
      };
      this.mediaRecorder = new MediaRecorder(this.canvasStream, options);
      this.recordedChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      this.mediaRecorder.start();
      this.isRecording = true;

      this.dataAvailableInterval = setInterval(() => {
        if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
          this.mediaRecorder.requestData();
        }
      }, 1000);

      console.log("MediaRecorder started.");
      return true;
    } catch (error) {
      console.error("Error in startRecording:", error);
      this.isRecording = false;
      return false;
    }
  }

  async stopRecording() {
    if (!this.isRecording || !this.mediaRecorder) {
      console.warn("Not recording, ignoring stopRecording call");
      return;
    }

    console.log("Stopping MediaRecorder...");
    if (this.dataAvailableInterval) {
      clearInterval(this.dataAvailableInterval);
      this.dataAvailableInterval = null;
    }

    // Attach onstop handler for normal flow
    this.mediaRecorder.onstop = async () => {
      console.log("MediaRecorder onstop event fired.");
      this.isRecording = false;
      await this._processRecordedChunks();
    };

    // If actively recording, request data and stop
    if (this.mediaRecorder.state === "recording") {
      try {
        this.mediaRecorder.requestData();
        setTimeout(() => {
          if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
            this.mediaRecorder.stop();
          }
        }, 100);
      } catch (e) {
        console.error("Error requesting final data:", e);
        this.mediaRecorder.stop();
      }
    } else {
      console.warn(
        `MediaRecorder.state is '${this.mediaRecorder.state}', manually processing.`
      );
      // Manually invoke processing since onstop won't fire
      await this._processRecordedChunks();
    }
  }

  async _processRecordedChunks() {
    if (this.recordedChunks.length === 0) {
      console.warn("No recorded chunks available");
      this.resetRecordingVariables();
      return;
    }

    const blob = new Blob(this.recordedChunks, {
      type: Settings.recording.mimeType,
    });
    try {
      console.log("Fixing video duration...");
      const fixedBlob = await this.videoProcessor.fixVideoDuration(blob); // Adjust duration
      const file = new File([fixedBlob], Settings.recording.outputFileName, {
        type: Settings.recording.mimeType,
      });
      const fileUrl = URL.createObjectURL(file);
      console.log("Processed video file URL:", fileUrl);
      setTimeout(() => this.onRecordingComplete(fileUrl), 100); // Pass the file URL to the callback
    } catch (error) {
      console.error("Error processing video blob:", error);
    } finally {
      this.resetRecordingVariables();
    }
  }

  onRecordingComplete(fileUrl) {
    console.log("Recording complete. File URL:", fileUrl);
  }

  resetRecordingVariables() {
    if (this.dataAvailableInterval) {
      clearInterval(this.dataAvailableInterval);
      this.dataAvailableInterval = null;
    }
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      try {
        this.mediaRecorder.stop();
      } catch {}
    }

    this.recordedChunks = [];
    this.isRecording = false;

    if (this.audioVideoStream) {
      this.audioVideoStream.getTracks().forEach((t) => t.stop());
      this.audioVideoStream = null;
    }
    if (this.canvasStream) {
      this.canvasStream.getTracks().forEach((t) => t.stop());
      this.canvasStream = null;
    }
    if (this.monitorNode) {
      try {
        this.monitorNode.disconnect();
      } catch {}
      this.monitorNode = null;
    }
    // Disconnect and clean up all audio nodes
    if (this.micSourceNode) {
      try { this.micSourceNode.disconnect(); } catch {}
      this.micSourceNode = null;
    }
    if (this.lensSourceNodes && this.lensSourceNodes.length > 0) {
      this.lensSourceNodes.forEach(node => { try { node.disconnect(); } catch {} });
      this.lensSourceNodes = [];
    }
    if (this.mixedDestination) {
      // No explicit disconnect needed, but clear reference
      this.mixedDestination = null;
    }
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
  }
}
